import Popover, { PopoverOrigin } from "@mui/material/Popover";
import cn from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import {
  TAB_GROUP_COLORS,
  TabGroup,
} from "pages/DevelopWorkspace/workspace.types";

import Button, { ButtonSize, ButtonTemplate } from "components/Button";
import { TextInput } from "components/TextInput/TextInput";

import styles from "./TabGroupPanel.module.scss";

interface TabGroupPanelProps {
  anchorEl: HTMLElement | null;
  attachTo: "left" | "right";
  tabGroup?: TabGroup;
  onClose: () => void;
  onSubmit: (tabGroup: { name: string; color: string }) => void;
}

const ORIGINS: {
  [key in TabGroupPanelProps["attachTo"]]: {
    anchorOrigin: PopoverOrigin;
    transformOrigin: PopoverOrigin;
  };
} = {
  right: {
    anchorOrigin: { vertical: "top", horizontal: "right" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  },
  left: {
    anchorOrigin: { vertical: "top", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "right" },
  },
};

const COLOR_NAME_KEYS: Record<string, string> = {
  "#3f8a8a": "tab_groups.colors.teal",
  "#4a7bb0": "tab_groups.colors.blue",
  "#5a63a8": "tab_groups.colors.indigo",
  "#7a5f9e": "tab_groups.colors.purple",
  "#96578c": "tab_groups.colors.plum",
};

const TabGroupPanel = (props: TabGroupPanelProps) => {
  const { anchorEl, attachTo, tabGroup, onClose, onSubmit } = props;
  const { t } = useTranslation();
  const {
    state: { tabGroups },
  } = useDocuments();

  const [name, setName] = useState(tabGroup?.name ?? "");
  const [color, setColor] = useState(tabGroup?.color ?? TAB_GROUP_COLORS[0]);

  const trimmedName = name.trim();

  // Group names are unique (case sensitive)
  const nameTaken = tabGroups.some(
    existingGroup =>
      existingGroup.name === trimmedName && existingGroup.id !== tabGroup?.id
  );

  const canSubmit = !!trimmedName && !nameTaken;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    onSubmit({ name: trimmedName, color });
  };

  return (
    <Popover
      open
      anchorEl={anchorEl}
      classes={{ paper: styles.paper }}
      onClose={onClose}
      onClick={event => {
        event.stopPropagation();
      }}
      {...ORIGINS[attachTo]}
    >
      <form
        className={styles.root}
        onSubmit={handleSubmit}
      >
        <div className={styles.title}>
          {tabGroup ? t("tab_groups.edit.title") : t("tab_groups.create.title")}
        </div>

        <TextInput
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the panel is opened by an explicit user action
          autoFocus
          label={t("tab_groups.create.name")}
          value={name}
          testId="tab-group-name-input"
          error={nameTaken}
          helperText={nameTaken ? t("tab_groups.create.name_taken") : undefined}
          onChange={event => setName(event.target.value)}
        />

        <div className={styles.colors}>
          {TAB_GROUP_COLORS.map(paletteColor => (
            <button
              key={paletteColor}
              type="button"
              aria-label={t(COLOR_NAME_KEYS[paletteColor] ?? paletteColor)}
              aria-pressed={paletteColor === color}
              data-testid={`tab-group-color-${paletteColor}`}
              className={cn(styles.color, {
                [styles.selected]: paletteColor === color,
              })}
              style={{ backgroundColor: paletteColor }}
              onClick={() => setColor(paletteColor)}
            />
          ))}
        </div>

        <div className={styles.buttons}>
          <Button
            text={t("tab_groups.create.cancel")}
            size={ButtonSize.Small}
            template={ButtonTemplate.Ghost}
            type="button"
            onClick={onClose}
            dataTestid="tab-group-cancel-btn"
          />
          <Button
            text={
              tabGroup
                ? t("tab_groups.edit.submit")
                : t("tab_groups.create.submit")
            }
            size={ButtonSize.Small}
            template={ButtonTemplate.Primary}
            disabled={!canSubmit}
            type="submit"
            dataTestid="tab-group-submit-btn"
          />
        </div>
      </form>
    </Popover>
  );
};

export default TabGroupPanel;
